import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { fmtBRL, fmtDate } from '../../lib/format';
import { Search, FileText } from 'lucide-react';
import { etapaLabel, etapaColor, statusFinLabel, statusFinColor, origemLabel } from './labels';

interface Contrato {
  id: string;
  numeroContrato: string;
  origemOperacional: string;
  valorTotal: number;
  mensalidadeAssociativa: number;
  qtdParcelas: number;
  qtdParcelasGeradas: number;
  etapaOperacional: string;
  statusFinanceiro: string;
  dataSolicitacao: string;
  associado: { id: string; nome: string; documentoMask: string } | null;
  agente: { id: string; nome: string } | null;
}

export function ContratosListaPage() {
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [etapa, setEtapa] = useState('');
  const [statusFin, setStatusFin] = useState('');
  const [page, setPage] = useState(1);
  const size = 50;

  const { data, isLoading } = useQuery({
    queryKey: ['contratos', { q: search, etapa, statusFin, page }],
    queryFn: () => {
      const params = new URLSearchParams({ size: String(size), page: String(page) });
      if (search) params.set('q', search);
      if (etapa) params.set('etapaOperacional', etapa);
      if (statusFin) params.set('statusFinanceiro', statusFin);
      return api<{ items: Contrato[]; total: number }>(`/contratos/?${params.toString()}`);
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Contratos</h1>
          <p className="text-muted-foreground">{data?.total ?? 0} contratos</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex gap-2 items-center flex-wrap">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por número, nome do associado ou CPF…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(q); setPage(1); } }}
              className="flex-1 min-w-[300px]"
            />
            <select
              value={etapa}
              onChange={(e) => { setEtapa(e.target.value); setPage(1); }}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Todas etapas</option>
              {Object.entries(etapaLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select
              value={statusFin}
              onChange={(e) => { setStatusFin(e.target.value); setPage(1); }}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Todos status</option>
              {Object.entries(statusFinLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <Button variant="secondary" onClick={() => { setSearch(q); setPage(1); }}>Buscar</Button>
            {(search || etapa || statusFin) && (
              <Button variant="ghost" size="sm" onClick={() => { setQ(''); setSearch(''); setEtapa(''); setStatusFin(''); setPage(1); }}>
                limpar
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && <Spinner />}
          {!isLoading && data && data.items.length === 0 && (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">
                Nenhum contrato. Cadastre um a partir da página do associado.
              </p>
            </div>
          )}
          {!isLoading && data && data.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-2 pr-3">Número</th>
                    <th className="text-left py-2 pr-3">Associado</th>
                    <th className="text-left py-2 pr-3">Origem</th>
                    <th className="text-right py-2 pr-3">Total</th>
                    <th className="text-right py-2 pr-3">Mensal.</th>
                    <th className="text-center py-2 pr-3">Parc.</th>
                    <th className="text-left py-2 pr-3">Etapa</th>
                    <th className="text-left py-2 pr-3">Status</th>
                    <th className="text-left py-2">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((c) => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-2 pr-3">
                        <Link to={`/contratos/${c.id}`} className="text-primary hover:underline font-mono text-xs">
                          {c.numeroContrato}
                        </Link>
                      </td>
                      <td className="py-2 pr-3">
                        {c.associado && (
                          <Link to={`/associados/${c.associado.id}`} className="hover:underline">
                            <div className="font-medium">{c.associado.nome}</div>
                            <div className="text-xs text-muted-foreground font-mono">{c.associado.documentoMask}</div>
                          </Link>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-xs">{origemLabel[c.origemOperacional] ?? c.origemOperacional}</td>
                      <td className="py-2 pr-3 text-right font-medium">{fmtBRL(c.valorTotal)}</td>
                      <td className="py-2 pr-3 text-right">{fmtBRL(c.mensalidadeAssociativa)}</td>
                      <td className="py-2 pr-3 text-center">{c.qtdParcelas}x</td>
                      <td className="py-2 pr-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${etapaColor[c.etapaOperacional] ?? 'bg-gray-100'}`}>
                          {etapaLabel[c.etapaOperacional] ?? c.etapaOperacional}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${statusFinColor[c.statusFinanceiro] ?? 'bg-gray-100'}`}>
                          {statusFinLabel[c.statusFinanceiro] ?? c.statusFinanceiro}
                        </span>
                      </td>
                      <td className="py-2 text-xs text-muted-foreground">{fmtDate(c.dataSolicitacao)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!isLoading && data && data.total > size && (
            <div className="flex items-center justify-between pt-4 text-sm">
              <span className="text-muted-foreground">
                Página {page} de {Math.ceil(data.total / size)} · {data.total} contratos
              </span>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
                <Button variant="secondary" size="sm" disabled={page >= Math.ceil(data.total / size)} onClick={() => setPage((p) => p + 1)}>Próxima</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
